import { CSSProperties } from 'react';
import clsx from 'clsx';
import { toChartTicks } from '@/utils/chart-scale';
import styles from './hours-chart.module.scss';

interface HoursChartProps {
  /** Names the chart for a screen reader, which the bars and hour ticks cannot. */
  label: string;
  /** One entry per bucket, in axis order. */
  values: number[];
  /** Omit both when today's window is unknown: nothing is then marked off-hours. */
  openHour?: number;
  closeHour?: number;
  /** One label per bucket. Defaults to hour numbers every third bucket. */
  labels?: string[];
  /** Buckets to shade, by index. Takes precedence over the open/close window. */
  offIndexes?: readonly number[];
  /** Chart height in px. Ignored when `fill` is set. */
  height?: number;
  /** Gridlines and their values down the left edge, so a bar can be read as a number. */
  showAxis?: boolean;
  /** Grow to the height of a flex-column parent instead of taking a fixed one. */
  fill?: boolean;
  className?: string;
}

const HOUR_LABEL_STEP = 3;

/** For a caller building the legend, which the mockup renders beside the chart. */
export const hoursChartColors = {
  inHours: styles.inHours,
  offHours: styles.offHours,
};

/**
 * Shading is driven by the caller's window so it cannot disagree with the caller's
 * "office hours" copy. An unknown window shades nothing rather than inventing one;
 * a known closure must pass a zero-width window, not omit it. A caller whose buckets
 * are not hours passes `offIndexes` instead, and its own labels.
 */
export default function HoursChart({
  values,
  label,
  openHour,
  closeHour,
  labels,
  offIndexes,
  height,
  showAxis = false,
  fill = false,
  className,
}: HoursChartProps) {
  const busiest = Math.max(...values, 0);
  const knowsWindow = openHour !== undefined && closeHour !== undefined;

  const isOff = (index: number) =>
    offIndexes
      ? offIndexes.includes(index)
      : knowsWindow && (index < openHour! || index >= closeHour!);

  const ticks = toChartTicks(busiest).map(({ value, fraction }) => ({
    value,
    at: fraction * 100,
  }));

  return (
    <div
      aria-label={label}
      className={clsx(styles.chart, fill && styles['chart-fill'], className)}
      role="img"
      style={height ? ({ '--hours-chart-height': `${height}px` } as CSSProperties) : undefined}
    >
      <div className={styles.plot} data-axis={showAxis}>
        {showAxis &&
          values.length > 0 &&
          ticks.map((tick) => (
            <div className={styles.gridline} key={tick.value} style={{ bottom: `${tick.at}%` }}>
              <span className={styles.tick}>{tick.value}</span>
            </div>
          ))}
        <div className={styles.columns}>
          {values.map((value, index) => (
            <div className={styles.col} key={index}>
              <div
                className={styles.bar}
                data-hour={index}
                data-off={isOff(index)}
                style={{ height: `${busiest ? Math.round((value / busiest) * 100) : 0}%` }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className={styles.labels} data-axis={showAxis}>
        {values.map((_, index) => (
          <span className={styles.hour} key={index}>
            {labels ? labels[index] : index % HOUR_LABEL_STEP === 0 ? index : ''}
          </span>
        ))}
      </div>
    </div>
  );
}
